import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight, ImageOff } from "lucide-react";
import { BLOG_POSTS, formatPostDate } from "../lib/blogPosts";
import { usePageMeta, SITE_URL } from "../hooks/usePageMeta";
import { useStructuredData } from "../hooks/useStructuredData";
import { PAGE_SEO } from "../lib/seo";

export default function Blog() {
  usePageMeta(PAGE_SEO.blog);
  useStructuredData("blog", {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Just Dessert Me Blog",
    url: `${SITE_URL}/blog`,
    blogPost: BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `${SITE_URL}/blog/${p.slug}`,
      ...(p.image ? { image: p.image } : {}),
    })),
  });

  return (
    <main data-testid="page-blog" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <p className="text-[0.7rem] uppercase tracking-[0.3em] text-[#00b4ff] mb-3">The sweet scoop</p>
      <h1 className="font-script text-5xl sm:text-6xl neon-gradient-text mb-4">Blog</h1>
      <p className="text-gray-400 max-w-2xl mb-12">
        Weekly stories from our Hillcrest kitchen — new flavours, behind-the-scenes peeks and dessert
        news from Hamilton's home of rolled ice cream.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {BLOG_POSTS.map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            data-testid={`blog-card-${post.slug}`}
            className="group glass rounded-3xl overflow-hidden border border-white/10 hover:border-[#ff1a8c]/50 transition-all flex flex-col"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              {post.image ? (
                <img
                  src={post.image}
                  alt={post.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a0510] to-[#0a0a10]">
                  <ImageOff className="w-8 h-8 text-[#ff1a8c]/60" />
                </div>
              )}
            </div>
            <div className="p-6 flex flex-col flex-1 gap-3">
              <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-gray-500">
                <CalendarDays className="w-3.5 h-3.5 text-[#00b4ff]" />
                {formatPostDate(post.date)}
              </p>
              <h2 className="font-script text-3xl text-white leading-tight">{post.title}</h2>
              <p className="text-sm text-gray-400 leading-relaxed flex-1">{post.excerpt}</p>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#ff1a8c] font-semibold mt-1">
                Read more <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}
